import { ITextFieldProps, TextField } from "@fluentui/react/lib/TextField";
import { formatMessage } from "../../utils/helpers/LanguageHelper";
import { IRenderFunction } from "@fluentui/react";
import React, { InputHTMLAttributes, RefAttributes } from "react";
import { IconButton } from "@fluentui/react/lib/Button";

const OnRenderInputWrapper: (
  enableButtons: boolean | "confirm" | "cancel" | "both",
  handleConfirm: () => void,
  handleCancel: () => void
) => IRenderFunction<InputHTMLAttributes<HTMLInputElement> & RefAttributes<HTMLInputElement>> =
  // eslint-disable-next-line react/display-name
  (enableButtons, handleConfirm, handleCancel) => (props, defaultRender) => {
    const confirmEnabled = enableButtons === true || enableButtons === "confirm" || enableButtons === "both";
    const cancelEnabled = enableButtons === true || enableButtons === "cancel" || enableButtons === "both";
    return (
      <>
        {defaultRender(props)}
        {confirmEnabled && (
          <IconButton aria-label="Confirm" iconProps={{ iconName: "Accept" }} onClick={handleConfirm} tabIndex={0} />
        )}
        {cancelEnabled && (
          <IconButton aria-label="Cancel" iconProps={{ iconName: "Cancel" }} onClick={handleCancel} tabIndex={0} />
        )}
      </>
    );
  };

export const ConfirmTextField = ({
  initialValue = "",
  placeholder = formatMessage("cof.base.insert"),
  sanitizeInput = false,
  onConfirm = () => {},
  onCancel = () => {},
  cancelIfEmpty = true,
  onUnmodifiedBehavior = "none",
  onBlurBehavior = "none",
  enableButtons = true,
  enableKeyboardShortcuts = true,
  ...textFieldProps
}: Omit<ITextFieldProps, "onRenderInput" | "value" | "style"> & {
  initialValue?: string;
  sanitizeInput?: false | ((value: string) => string);
  onConfirm?: (oldValue: string, newValue: string) => void;
  onCancel?: (oldValue: string) => void;
  cancelIfEmpty?: boolean;
  onUnmodifiedBehavior?: "confirm" | "cancel" | "none";
  onBlurBehavior?: "confirm" | "cancel" | "none";
  enableButtons?: boolean | "confirm" | "cancel" | "both";
  enableKeyboardShortcuts?: boolean | "enter" | "escape" | "both";
}) => {
  const [currentValue, setCurrentValue] = React.useState(initialValue);
  const [blurred, setBlurred] = React.useState(true);

  const handleConfirm = () => {
    const newValue = !sanitizeInput ? currentValue : sanitizeInput(currentValue);
    setCurrentValue(newValue);
    if (cancelIfEmpty && newValue === "") {
      handleCancel();
      return;
    }

    if (newValue === initialValue) {
      switch (onUnmodifiedBehavior) {
        case "confirm":
          break;
        case "cancel":
          handleCancel();
        // eslint-disable-next-line no-fallthrough
        case "none":
        default:
          return;
      }
    }

    onConfirm(initialValue, newValue);
  };

  const handleCancel = () => {
    setCurrentValue(initialValue);
    onCancel(initialValue);
  };

  return (
    <fieldset
      tabIndex={-1}
      onBlur={(e) => {
        if (e.currentTarget.contains(e.relatedTarget)) {
          return;
        }
        setBlurred(true);
        if (onBlurBehavior === "confirm") {
          handleConfirm();
        } else if (onBlurBehavior === "cancel") {
          handleCancel();
        }
      }}
      style={{
        flex: 1,
        width: "100%",
        minWidth: "100px",
      }}
    >
      <TextField
        tabIndex={0}
        onRenderInput={OnRenderInputWrapper(enableButtons, handleConfirm, handleCancel)}
        autoFocus={true}
        onFocus={(e) => {
          if (blurred) {
            e.currentTarget.select();
          }
          setBlurred(false);
        }}
        placeholder={placeholder}
        value={currentValue}
        onChange={(_e, newValue) => setCurrentValue(newValue)}
        onKeyDown={(e) => {
          const enterEnabled =
            enableKeyboardShortcuts === true ||
            enableKeyboardShortcuts === "enter" ||
            enableKeyboardShortcuts === "both";
          const escapeEnabled =
            enableKeyboardShortcuts === true ||
            enableKeyboardShortcuts === "escape" ||
            enableKeyboardShortcuts === "both";

          e.stopPropagation();
          if (enterEnabled && e.key === "Enter") {
            handleConfirm();
          } else if (escapeEnabled && e.key === "Escape") {
            handleCancel();
          }
        }}
        style={{
          flex: 1,
          width: "100%",
          minWidth: "100px",
        }}
        {...textFieldProps}
      />
    </fieldset>
  );
};
